import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import * as LuIcons from 'react-icons/lu';
const { LuFacebook, LuLinkedin, LuMail, LuMapPin, LuPhone, LuSend, LuCheckCircle, LuAlertCircle } = LuIcons;
import SafeIcon from './common/SafeIcon';
import { CONTACT_INFO, NAV_LINKS } from '../constants';
import { NewsletterSubmission } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  const [newsStatus, setNewsStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const { t, language } = useLanguage();

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNewsStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get('email') as string;

    try {
      const submission: NewsletterSubmission = {
        id: Date.now().toString(),
        email: email,
        date: new Date().toISOString()
      };

      const existing = JSON.parse(localStorage.getItem('be_newsletters') || '[]');
      if (!existing.some((n: any) => n.email === email)) {
        localStorage.setItem('be_newsletters', JSON.stringify([...existing, submission]));
      }

      setTimeout(() => {
        setNewsStatus('success');
        form.reset();
        setTimeout(() => setNewsStatus('idle'), 5000);
      }, 800);
    } catch (err) {
      setNewsStatus('error');
      setTimeout(() => setNewsStatus('idle'), 3000);
    }
  };

  return (
    <footer className="bg-gradient-to-b from-be-darkest to-[#162238] text-white pt-16 pb-8 font-sans border-t border-white/5">
      <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-12 mb-16">
          {/* Colonne 1 : Branding & Slogan */}
          <div className="space-y-6">
            <RouterLink to="/" className="inline-block">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1775921981458-Logo%20BE%20consulting%20final.PNG" 
                alt="BE Consulting Logo" 
                className="h-10 w-auto object-contain brightness-110" 
                loading="lazy" 
              />
            </RouterLink>
            <div className="space-y-2">
              <p className="text-be-sky font-heading font-bold text-sm tracking-wider uppercase italic">
                When Mission Meets Value
              </p>
              <p className="text-[#94a3b8] text-sm leading-relaxed pr-4">
                {t('footerDescription')}
              </p>
            </div>
            <div className="flex gap-4 pt-2">
              <a href="https://linkedin.com/company/be-consulting" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#1e293b] flex items-center justify-center hover:bg-[#3b82f6] transition-colors text-white" aria-label="LinkedIn">
                <SafeIcon icon={LuLinkedin} size={18} />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#1e293b] flex items-center justify-center hover:bg-[#3b82f6] transition-colors text-white" aria-label="Facebook">
                <SafeIcon icon={LuFacebook} size={18} />
              </a>
            </div>
          </div>

          {/* Colonne 2 : Navigation */}
          <div>
            <div className="mb-6">
              <h4 className="text-xl font-bold text-white inline-block border-b-[3px] border-[#2563eb] pb-2 pr-6">
                {t('footerNavigation')}
              </h4>
            </div>
            <ul className="space-y-4 text-[15px] text-[#cbd5e1] font-medium">
              {NAV_LINKS[language].map((link) => (
                <li key={link.path}>
                  <RouterLink to={link.path} className="hover:text-white transition-colors">
                    {link.label}
                  </RouterLink>
                </li>
              ))}
              <li><RouterLink to="/legal" className="hover:text-white transition-colors">{t('legalMentions')}</RouterLink></li>
            </ul>
          </div>

          {/* Colonne 3 : Contact */}
          <div>
            <div className="mb-6">
              <h4 className="text-xl font-bold text-white inline-block border-b-[3px] border-[#2563eb] pb-2 pr-6">
                {t('footerContactUs')}
              </h4>
            </div>
            <ul className="space-y-5 text-[14px] text-[#cbd5e1]">
              <li className="flex items-start gap-4">
                <SafeIcon icon={LuMapPin} className="text-[#3b82f6] mt-1 shrink-0" size={20} />
                <span className="leading-relaxed">{CONTACT_INFO.address}</span>
              </li>
              <li className="flex items-center gap-4">
                <SafeIcon icon={LuMail} className="text-[#3b82f6] shrink-0" size={20} />
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-white transition-colors">{CONTACT_INFO.email}</a>
              </li>
              <li className="flex items-start gap-4">
                <SafeIcon icon={LuPhone} className="text-[#3b82f6] mt-1 shrink-0" size={20} />
                <div className="flex flex-col gap-1">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-white/50 uppercase tracking-tighter">
                      {t('whatsappTelLabel')}
                    </span>
                    {CONTACT_INFO.phones.map((phone, idx) => (
                      <a key={idx} href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Colonne 4 : Newsletter */}
          <div>
            <div className="mb-6">
              <h4 className="text-xl font-bold text-white inline-block border-b-[3px] border-[#2563eb] pb-2 pr-6">
                {t('footerNewsletter')}
              </h4>
            </div>
            <div className="bg-[#1e293b]/50 backdrop-blur-sm rounded-xl p-6 border border-[#334155]/50">
              <h5 className="font-bold text-white mb-3 text-[15px]">{t('stayInformed')}</h5>
              <p className="text-sm text-[#94a3b8] mb-5 leading-relaxed">
                {t('newsletterDescImg')}
              </p>
              {newsStatus === 'success' ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-green-900/30 text-green-400 p-3 rounded-lg flex items-center gap-2 text-sm border border-green-800/50">
                  <SafeIcon icon={LuCheckCircle} size={18} />
                  {t('subscriptionSuccess')}
                </motion.div>
              ) : (
                <form onSubmit={handleNewsletter} className="space-y-3">
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder={t('newsletterPlaceholder')}
                    className="w-full bg-[#0f172a] border border-[#334155] text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#3b82f6] transition-colors text-sm placeholder-[#64748b]"
                    disabled={newsStatus === 'submitting'}
                  />
                  <button
                    type="submit"
                    disabled={newsStatus === 'submitting'}
                    className="w-full bg-[#3b82f6] hover:bg-[#2563eb] text-white text-sm font-bold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
                  >
                    {newsStatus === 'submitting' ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    ) : (
                      <>{t('subscribeBtn')} <SafeIcon icon={LuSend} size={16} /></>
                    )}
                  </button>
                  {newsStatus === 'error' && (
                    <p className="text-red-400 text-xs flex items-center gap-1 mt-1">
                      <SafeIcon icon={LuAlertCircle} size={14} />
                      {t('formError')}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Barre de Copyright Basse */}
        <div className="border-t border-white/5 pt-8 pb-4 flex flex-col items-center justify-center gap-8">
          {/* Liens légaux */}
          <div className="flex flex-wrap justify-center items-center gap-4 text-sm text-[#cbd5e1]">
            <RouterLink to="/legal" className="hover:text-white transition-colors">{t('legalMentions')}</RouterLink>
            <span className="text-[#3b82f6] text-xl leading-none">•</span>
            <RouterLink to="/privacy" className="hover:text-white transition-colors">{t('privacyPolicy')}</RouterLink>
            <span className="text-[#3b82f6] text-xl leading-none">•</span>
            <RouterLink to="/terms" className="hover:text-white transition-colors">{t('termsOfService')}</RouterLink>
          </div>

          {/* Copyright & Crédits */}
          <div className="text-center space-y-3">
            <p className="text-[14px] text-[#64748b]">
              &copy; {new Date().getFullYear()} BE Consulting & Services. {t('allRightsReserved')}
            </p>
            <p className="text-[13px] text-[#475569] flex items-center justify-center gap-1.5">
              {t('designedWith')} <span className="text-red-500 text-sm">❤️</span> {t('by')} <a href="https://netvisible.net" target="_blank" rel="noopener noreferrer" className="hover:text-[#cbd5e1] transition-colors">netvisible.net</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;